/**
 * EditShiftModal  (common/modal/EditShiftModal.jsx)
 *
 * Day-scoped shift editor opened by clicking a shift chip on any Schedule calendar cell.
 * ONE instance serves all six calendar views (Practice/Employee × Day/Week/Month) — the
 * caller holds a single "which shift was clicked" state object and renders this once.
 *
 * What it writes — `practitioner_schedule_overrides` ONLY:
 *   Save          → POST/PATCH { practitioner, date_ranges:[{D,D}], is_open:true,  start_time, end_time }
 *   Delete shift  → POST/PATCH { practitioner, date_ranges:[{D,D}], is_open:false, start_time:"", end_time:"" }
 *   Reset         → pure client state; reverts the inputs, no request
 *
 * The recurring template (`practitioner_weekly_schedule`) is NEVER touched, which is what
 * keeps "every Monday 8–5" intact when one Monday is changed. Overrides sit above the
 * weekly schedule in the availability chain (see availability.py), so a single-date row
 * wins for that date and that date only.
 *
 * Upsert, not blind create: when the clicked cell already has an override row the caller
 * passes it as `overrideRow` and we PATCH it. Without this, two rows for the same date
 * both persist and the backend silently picks the first by `-created_at`.
 *
 * Roles: the caller gates rendering on `isManagerOrAbove` (1–3) to mirror the backend's
 * `has_role([1, 2, 3])` on PractitionerScheduleOverrideViewSet. Practitioner Lead (4) is
 * deliberately excluded — it can manage clinic closures and approve vacation, but has
 * never been able to write practitioner overrides, so showing it these buttons would 403.
 */
import { useEffect, useMemo, useState } from "react";
import Modal from "./Modal";
import Button from "../ui/Button";
import Dropdown from "../form/Dropdown";
import { SCHEDULE_TIMES } from "../scheduleUtils";
import { slotHours } from "@utils/weeklySchedule";

// Quick presets from the design. Kept here rather than in scheduleUtils because they are
// this editor's affordance, not a schedule-wide concept.
export const SHIFT_PRESETS = [
  { label: "Morning", start: "8:00 AM", end: "12:00 PM" },
  { label: "Mid-day", start: "10:00 AM", end: "2:00 PM" },
  { label: "Evening", start: "1:00 PM", end: "5:00 PM" },
  { label: "Half day", start: "8:00 AM", end: "12:00 PM" },
];

function formatShiftDate(dateStr) {
  if (!dateStr) return "";
  // Parse as local, not UTC — `new Date("2026-06-04")` is midnight UTC and renders as the
  // previous day west of Greenwich.
  const [y, m, d] = dateStr.split("-").map(Number);
  return new Date(y, m - 1, d).toLocaleDateString("en-US", {
    weekday: "long",
    month: "long",
    day: "numeric",
    year: "numeric",
  });
}

export default function EditShiftModal({
  isOpen,
  onClose,
  practitionerName = "",
  dateStr = "",
  slot = null,
  overrideRow = null,
  onSave,
  onDelete,
  isSaving = false,
  isDeleting = false,
  error = "",
}) {
  const initialStart = slot?.start || "8:00 AM";
  const initialEnd = slot?.end || "5:00 PM";

  const [start, setStart] = useState(initialStart);
  const [end, setEnd] = useState(initialEnd);

  // Re-seed on every open. The component stays mounted between openings, so without this
  // the inputs keep whatever was last typed. `isOpen` must be in the dep list: editing
  // 8–5 to 10–6, closing without saving, then reopening the SAME cell leaves every other
  // dep unchanged, so the effect would not re-run and the form would still read 10–6.
  useEffect(() => {
    if (!isOpen) return;
    setStart(slot?.start || "8:00 AM");
    setEnd(slot?.end || "5:00 PM");
  }, [isOpen, slot?.start, slot?.end, dateStr, practitionerName]);

  // An override carries no breaks, so slotHours is the plain span: 8 AM–5 PM → 9 h.
  const hours = useMemo(() => slotHours({ start, end }), [start, end]);
  const isDirty = start !== initialStart || end !== initialEnd;
  // Only equal times are rejected. An end BEFORE the start is a legitimate overnight
  // shift — slotHours adds a day when it crosses midnight (5 PM–8 AM = 15 h), matching
  // the weekly-schedule editor. Equal times would silently save as a 24 h shift.
  const invalidRange = start === end;
  const busy = isSaving || isDeleting;
  // No slot means the day currently reads "Off" — either the recurring template doesn't
  // cover it, or a previous "Delete shift" wrote an is_open:false override. Either way this
  // is an add, so there is nothing to delete and the copy should say so. Saving reuses the
  // existing override row when one is present, which is how a deleted day is restored.
  const isAdding = !slot?.start || !slot?.end;

  function applyPreset(preset) {
    setStart(preset.start);
    setEnd(preset.end);
  }

  function handleReset() {
    setStart(initialStart);
    setEnd(initialEnd);
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={isAdding ? "Add shift" : "Edit shift"}
      subtitle={[practitionerName, formatShiftDate(dateStr)].filter(Boolean).join(" · ")}
      size="md"
      footer={
        <div className="flex w-full flex-col-reverse gap-3 sm:flex-row sm:items-center sm:justify-between">
          {isAdding ? (
            <span />
          ) : (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={onDelete}
              disabled={busy}
              className="gap-1.5 text-palette-red-label hover:bg-palette-red-soft"
            >
              🗑 {isDeleting ? "Removing..." : "Delete shift"}
            </Button>
          )}

          <div className="flex flex-col-reverse gap-3 sm:flex-row sm:items-center">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={handleReset}
              disabled={busy || !isDirty}
              className="gap-1.5"
            >
              ↺ Reset
            </Button>
            <Button
              type="button"
              variant="primary"
              size="sm"
              onClick={() => onSave({ start, end })}
              disabled={busy || invalidRange}
              className="gap-1.5"
            >
              ✓ {isSaving ? "Saving..." : isAdding ? "Add shift" : "Save shift"}
            </Button>
          </div>
        </div>
      }
    >
      <div className="grid gap-4 sm:grid-cols-2">
        <Dropdown label="Starts" options={SCHEDULE_TIMES} value={start} onChange={setStart} />
        <Dropdown label="Ends" options={SCHEDULE_TIMES} value={end} onChange={setEnd} />
      </div>

      <div className="mt-3 flex flex-wrap gap-2">
        {SHIFT_PRESETS.map((preset) => {
          const active = start === preset.start && end === preset.end;
          return (
            <button
              key={preset.label}
              type="button"
              onClick={() => applyPreset(preset)}
              className={`rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${
                active
                  ? "border-palette-blue-indicator bg-palette-blue-soft text-palette-blue-heading"
                  : "border-palette-gray-divider text-palette-slate-badge hover:bg-palette-gray-subtle"
              }`}
            >
              {preset.label}
            </button>
          );
        })}
      </div>

      <div className="mt-4 rounded-xl bg-palette-blue-soft px-4 py-3 text-sm font-medium text-palette-blue-heading">
        {invalidRange ? (
          <span className="text-palette-red-label">Start and end time cannot be the same.</span>
        ) : (
          <>
            {start} – {end}
            {hours !== null && <> · {hours} h scheduled</>}
          </>
        )}
      </div>

      {overrideRow && (
        <p className="mt-2 text-xs text-palette-gray-placeholder">
          {isAdding
            ? "This date was previously marked off — saving restores it with these hours."
            : "This date already has a one-day override — saving updates it instead of adding another."}
        </p>
      )}

      <p className="mt-2 text-xs text-palette-gray-placeholder">
        Changes apply to this date only. The recurring weekly schedule is not affected.
      </p>

      {error && <p className="mt-2 text-xs text-palette-red-label">{error}</p>}
    </Modal>
  );
}
